import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Badge,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Spinner,
    Table
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import { getReputationConfig, updateReputationConfig } from "../../api/ApiServices";
import Swal from "sweetalert2";

const ReputationConfig = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [badgeConfig, setBadgeConfig] = useState(null);
    const [tierConfig, setTierConfig] = useState(null);
    const [reactionWeights, setReactionWeights] = useState(null);
    const [rateLimits, setRateLimits] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const res = await getReputationConfig();
            if (res.status && res.payload.configs) {
                const bConfig = res.payload.configs.find(c => c.configKey === "BADGE_LIMITS");
                const tConfig = res.payload.configs.find(c => c.configKey === "TIER_LIMITS");
                const rWeights = res.payload.configs.find(c => c.configKey === "REACTION_WEIGHTS");
                const rLimits = res.payload.configs.find(c => c.configKey === "RATE_LIMITS");

                if (bConfig) setBadgeConfig(bConfig.configValue);
                if (tConfig) setTierConfig(tConfig.configValue);
                if (rWeights) setReactionWeights(rWeights.configValue);
                if (rLimits) setRateLimits(rLimits.configValue);
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to fetch configuration", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleBadgeChange = (badgeId, level, value) => {
        setBadgeConfig({
            ...badgeConfig,
            [badgeId]: {
                ...badgeConfig[badgeId],
                [level]: parseInt(value) || 0
            }
        });
        if (errors[`${badgeId}_${level}`]) {
            setErrors({ ...errors, [`${badgeId}_${level}`]: null });
        }
    };

    const handleTierChange = (index, field, value) => {
        const newTiers = [...tierConfig];
        newTiers[index] = {
            ...newTiers[index],
            [field]: field === 'limit' ? (value === '' ? '' : parseInt(value) || 0) : value
        };
        setTierConfig(newTiers);
    };

    const addTier = () => {
        setTierConfig([...tierConfig, { name: '', limit: '' }]);
    };

    const removeTier = (index) => {
        const newTiers = [...tierConfig];
        newTiers.splice(index, 1);
        setTierConfig(newTiers);
    };

    const saveBadgeConfig = async () => {
        let newErrors = {};
        for (const [badgeId, levels] of Object.entries(badgeConfig)) {
            const bronze = parseInt(levels['bronze']) || 0;
            const silver = parseInt(levels['silver']) || 0;
            const gold = parseInt(levels['gold']) || 0;

            if (bronze < 0) newErrors[`${badgeId}_bronze`] = "Minimum allowed value is 0.";
            if (silver < 0) newErrors[`${badgeId}_silver`] = "Minimum allowed value is 0.";
            else if (silver <= bronze) newErrors[`${badgeId}_silver`] = "Must be greater than Bronze limit.";
            if (gold < 0) newErrors[`${badgeId}_gold`] = "Minimum allowed value is 0.";
            else if (gold <= silver) newErrors[`${badgeId}_gold`] = "Must be greater than Silver limit.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setSaving(true);
        try {
            const res = await updateReputationConfig({
                configKey: "BADGE_LIMITS",
                configValue: badgeConfig,
                description: "Points required for each badge level"
            });
            if (res.status) {
                Swal.fire({
                    title: "Success",
                    text: "Badge thresholds updated successfully",
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (err) {
            Swal.fire("Error", "Failed to update badge configuration", "error");
        } finally {
            setSaving(false);
        }
    };

    const saveTierConfig = async () => {
        // Validation for tiers
        for (let i = 0; i < tierConfig.length; i++) {
            if (!tierConfig[i].name) {
                Swal.fire("Error", `Tier name cannot be empty for tier ${i + 1}`, "error");
                return;
            }
            if (i < tierConfig.length - 1 && (tierConfig[i].limit === '' || isNaN(tierConfig[i].limit))) {
                Swal.fire("Error", `Please provide a valid score limit for tier ${tierConfig[i].name}`, "error");
                return;
            }
        }

        const limits = tierConfig.slice(0, -1).map(t => parseInt(t.limit));
        const uniqueLimits = new Set(limits);
        if (uniqueLimits.size !== limits.length) {
            Swal.fire("Error", "Tier limits must be unique. You cannot have the same range for different tiers.", "error");
            return;
        }

        const sortedLimits = [...limits].sort((a, b) => a - b);
        for (let i = 0; i < limits.length; i++) {
            if (limits[i] !== sortedLimits[i]) {
                Swal.fire("Error", "Tier limits must be in ascending order.", "error");
                return;
            }
        }

        // The last tier doesn't need a strict limit since it catches everything above, use a high number for DB storage
        const finalTierConfig = tierConfig.map((t, idx) => ({
            name: t.name,
            limit: idx === tierConfig.length - 1 ? 1000000000 : parseInt(t.limit)
        }));

        setSaving(true);
        try {
            const res = await updateReputationConfig({
                configKey: "TIER_LIMITS",
                configValue: finalTierConfig,
                description: "Reputation score thresholds for tiers"
            });
            if (res.status) {
                Swal.fire({
                    title: "Success",
                    text: "Tier limits updated successfully",
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (err) {
            Swal.fire("Error", "Failed to update tier configuration", "error");
        } finally {
            setSaving(false);
        }
    };

    const saveReactionSettings = async () => {
        let newErrors = {};
        for (const [type, weight] of Object.entries(reactionWeights)) {
            const isPenalty = type === "Moderated Message Penalty" || type === "postDislike";
            if (!isPenalty && weight < 0) {
                newErrors[type] = "Minimum value is 0.";
            } else if (isPenalty && weight > 0) {
                newErrors[type] = "Must be 0 or a negative value.";
            }
        }

        const maxR = parseInt(rateLimits?.maxReactionsPerDay) || 0;
        if (maxR < 1) newErrors.maxReactionsPerDay = "Must be 1 or greater.";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setSaving(true);
        try {
            const res1 = await updateReputationConfig({
                configKey: "REACTION_WEIGHTS",
                configValue: reactionWeights,
                description: "Points awarded for each reaction type"
            });
            const res2 = await updateReputationConfig({
                configKey: "RATE_LIMITS",
                configValue: rateLimits,
                description: "Daily limits for user actions"
            });
            if (res1.status && res2.status) {
                Swal.fire({
                    title: "Success",
                    text: "Reaction settings updated successfully",
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (err) {
            Swal.fire("Error", "Failed to update reaction settings", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
                <CommonHeader />
                <Container className="mt--7" fluid>
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-white">Loading configuration...</p>
                    </div>
                </Container>
            </>
        );
    }

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    <Col xl="6">
                        <Card className="shadow border-0">
                            <CardHeader className="bg-transparent">
                                <Row className="align-items-center">
                                    <div className="col">
                                        <h6 className="text-uppercase text-muted ls-1 mb-1">Reputation Balancing</h6>
                                        <h3 className="mb-0">Badge Thresholds</h3>
                                    </div>
                                    <div className="col text-right">
                                        <Button color="primary" size="sm" onClick={saveBadgeConfig} disabled={saving}>
                                            {saving ? <Spinner size="sm" /> : "Save Changes"}
                                        </Button>
                                    </div>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <Form>
                                    {badgeConfig && Object.entries(badgeConfig).map(([badgeId, levels]) => (
                                        <div key={badgeId} className="mb-4 pb-3 border-bottom last-child-no-border">
                                            <h5 className="text-primary text-uppercase mb-3">
                                                <i className="ni ni-badge mr-2" />
                                                {badgeId.replace('_', ' ')}
                                            </h5>
                                            <Row>
                                                {Object.entries(levels).map(([level, value]) => (
                                                    <Col md="4" key={level}>
                                                        <FormGroup>
                                                            <label className="form-control-label text-capitalize">
                                                                {level} Limit
                                                            </label>
                                                            <InputGroup className="input-group-merge">
                                                                <Input
                                                                    type="number"
                                                                    value={value}
                                                                    invalid={!!errors[`${badgeId}_${level}`]}
                                                                    onChange={(e) => handleBadgeChange(badgeId, level, e.target.value)}
                                                                />
                                                                <InputGroupAddon addonType="append">
                                                                    <InputGroupText><i className="fas fa-plus text-xs" /></InputGroupText>
                                                                </InputGroupAddon>
                                                            </InputGroup>
                                                            {errors[`${badgeId}_${level}`] && <small className="text-danger d-block mt-1">{errors[`${badgeId}_${level}`]}</small>}
                                                        </FormGroup>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    ))}
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col xl="6">
                        <Card className="shadow border-0">
                            <CardHeader className="bg-transparent">
                                <Row className="align-items-center">
                                    <div className="col">
                                        <h6 className="text-uppercase text-muted ls-1 mb-1">Progression Balancing</h6>
                                        <h3 className="mb-0">Tier Score Limits</h3>
                                    </div>
                                    <div className="col d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
                                        {/* <Button color="success" size="sm" onClick={addTier} disabled={saving}>
                                            <i className="fas fa-plus mr-1" /> Add Tier
                                        </Button> */}
                                        {/* <Button color="info" size="sm" onClick={saveTierConfig} disabled={saving}>
                                            {saving ? <Spinner size="sm" /> : "Save Changes"}
                                        </Button> */}
                                    </div>
                                </Row>
                            </CardHeader>
                            <CardBody className="p-0">
                                <div className="table-responsive">
                                    <Table className="align-items-center table-flush mb-0" responsive>
                                        <thead className="thead-light">
                                            <tr>
                                                <th scope="col" style={{ borderTop: "none", padding: "16px 24px" }}>Tier Rank Name</th>
                                                <th scope="col" style={{ borderTop: "none" }}>Score Limit (Up to)</th>
                                                <th scope="col" className="text-right" style={{ borderTop: "none", paddingRight: "24px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tierConfig && tierConfig.map((tier, index) => (
                                                <tr key={index}>
                                                    <td style={{ padding: "12px 24px" }}>
                                                        <Input
                                                            type="text"
                                                            className="form-control-alternative font-weight-600 text-dark"
                                                            style={{ borderRadius: "8px" }}
                                                            value={tier.name}
                                                            placeholder="e.g. Newbie"
                                                            disabled
                                                            onChange={(e) => handleTierChange(index, 'name', e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{ padding: "12px 10px" }}>
                                                        <InputGroup className="input-group-merge alternative-group">
                                                            <Input
                                                                type="text"
                                                                className="form-control-alternative font-weight-600 text-dark"
                                                                style={{ borderRadius: "8px 0 0 8px" }}
                                                                value={index === tierConfig.length - 1 ? "No Limit" : (tier.limit === 1000000000 ? '' : tier.limit)}
                                                                disabled
                                                                placeholder="e.g. 100"
                                                                onChange={(e) => handleTierChange(index, 'limit', e.target.value)}
                                                            />
                                                            <InputGroupAddon addonType="append">
                                                                <InputGroupText style={{ borderRadius: "0 8px 8px 0", borderLeft: "none" }}>
                                                                    <Badge color={
                                                                        index === 0 ? "success" :
                                                                            index === 1 ? "primary" :
                                                                                index === 2 ? "info" :
                                                                                    index === 3 ? "warning" : "danger"
                                                                    } pill>
                                                                        {tier.name || "New Tier"}
                                                                    </Badge>
                                                                </InputGroupText>
                                                            </InputGroupAddon>
                                                        </InputGroup>
                                                    </td>
                                                    <td className="text-right" style={{ paddingRight: "24px" }}>
                                                        {/* {tierConfig.length > 1 && (
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                outline
                                                                className="btn-icon btn-inner--icon"
                                                                onClick={() => removeTier(index)}
                                                            >
                                                                <i className="fas fa-trash" />
                                                            </Button>
                                                        )} */}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <div className="p-3 bg-secondary rounded border m-3">
                                    <small className="text-muted d-block">
                                        <i className="fas fa-info-circle mr-2 text-info" />
                                        Users are assigned to the lowest tier whose limit is greater than or equal to their reputation score. The final tier has no upper limit.
                                    </small>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Row className="mt-4">
                    <Col xl="12">
                        <Card className="shadow border-0">
                            <CardHeader className="bg-transparent">
                                <Row className="align-items-center">
                                    <div className="col">
                                        <h6 className="text-uppercase text-muted ls-1 mb-1">Interaction Balancing</h6>
                                        <h3 className="mb-0">Reaction Weights & Limits</h3>
                                    </div>
                                    <div className="col text-right">
                                        <Button color="success" size="sm" onClick={saveReactionSettings} disabled={saving}>
                                            {saving ? <Spinner size="sm" /> : "Save Settings"}
                                        </Button>
                                    </div>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="8">
                                        <h5 className="text-muted text-uppercase mb-3">Reaction Points</h5>
                                        <Row>
                                            {reactionWeights && Object.entries(reactionWeights).map(([type, weight]) => {
                                                const isPenalty = type === "Moderated Message Penalty" || type === "postDislike";
                                                const getDisplayLabel = (key) => {
                                                    if (key === "postLike") return "Community Post Like";
                                                    if (key === "postDislike") return "Community Post Dislike";
                                                    return key;
                                                };
                                                return (
                                                    <Col md="4" key={type} className="mb-3">
                                                        <FormGroup>
                                                            <label className="form-control-label">{getDisplayLabel(type)}</label>
                                                            <Input
                                                                type="number"
                                                                value={weight}
                                                                invalid={!!errors[type]}
                                                                onChange={(e) => {
                                                                    let val = parseInt(e.target.value) || 0;
                                                                    setReactionWeights({ ...reactionWeights, [type]: val });
                                                                    if (errors[type]) setErrors({ ...errors, [type]: null });
                                                                }}
                                                            />
                                                            {errors[type] && <small className="text-danger d-block mt-1">{errors[type]}</small>}
                                                            {isPenalty && !errors[type] && (
                                                                <small className="text-danger d-block mt-1">
                                                                    {type === "postDislike"
                                                                        ? "Points will be deducted from user's reputation"
                                                                        : `Penalty points will be deducted from user's reputation`}
                                                                </small>
                                                            )}
                                                        </FormGroup>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    </Col>
                                    <Col md="4" className="border-left">
                                        <h5 className="text-muted text-uppercase mb-3">Anti-Spam Limits</h5>
                                        <FormGroup>
                                            <label className="form-control-label">Max Reactions Per Day</label>
                                            <Input
                                                type="number"
                                                value={rateLimits?.maxReactionsPerDay || 0}
                                                invalid={!!errors.maxReactionsPerDay}
                                                onChange={(e) => {
                                                    setRateLimits({ ...rateLimits, maxReactionsPerDay: parseInt(e.target.value) || 0 });
                                                    if (errors.maxReactionsPerDay) setErrors({ ...errors, maxReactionsPerDay: null });
                                                }}
                                            />
                                            {errors.maxReactionsPerDay && <small className="text-danger d-block mt-1">{errors.maxReactionsPerDay}</small>}
                                            <small className="text-muted">Prevents reputation farming by limiting daily interactions.</small>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default ReputationConfig;
